/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import {
  CONFIGUREROUTES,
  DATEFORMATS,
  DEVICELIST,
  PROVISION_CHECK_VAL,
  ROUTES,
  SCOS_VERSION_23_4_0,
  SEVERITYLIST,
  STATUSLIST,
  VERSIONLIST,
} from "../../globals/globals";
import {
  INrCell,
  ILteCell,
  IRadioNodes,
  IIPInterface,
  IngpeerInterfaces,
  IltepeerInterfaces,
  IFullDeviceDetails,
  IRfFeed,
  IRn620,
  IZoneDevice,
  IRadioNodesDataGridRow,
  ICellListData,
} from "../../store/api/deviceConfig/types";
import {
  IInventory,
  IRadioInputModule,
  IRowDataGridPro,
  ISectorDeviceTree,
  ICommissioningData,
  IRadioNodeCellConfig,
  IRnLteCellConfig,
  IRnCellInfo,
  IdeviceCounts,
  IdrawerCounts,
  IDuCellInfo,
} from "../../store/api/devices/types";
import { ITaskDetails, ITaskLogData, ITaskLogDetailsPayload, ITemplates } from "../../store/api/tasks/types";
import dayjs from "dayjs";
import { DasModelNames, repeatOptions } from "../../globals/constants";
import { handleBooleanStringValues, transfomDetail } from "../../utils/helpers";
import { IDataGrid, IDataGridItem } from "./types";
import { ICustomEvent } from "../../store/api/admin/types";
import { IOption } from "../../components/Shared/Filter/types";
import { IKeyValueAny } from "../../components/Administrations/types";

export const getSelectedIndex = (location: any) => {
  const locationArray: Array<string> = location?.pathname.split("/");
  const locationPath = locationArray.length <= 3 ? "" : location?.pathname.split("/")[3];
  let names;
  let configGroups;
  if (location?.pathname === `/${ROUTES.NODES.Path}`) {
    names = Object.getOwnPropertyNames(DEVICELIST);
    configGroups = names.map((name) => ({ name, ...DEVICELIST[name] }));
  } else {
    names = Object.getOwnPropertyNames(CONFIGUREROUTES);
    configGroups = names.map((name) => ({ name, ...CONFIGUREROUTES[name] }));
  }

  const activeIndex = configGroups.filter((item: any) => item?.Path === locationPath)[0].Key;

  return activeIndex;
};

export const getConfigRoutesByNodeType = (nodeType: string, swVersion: string, modelName: string, configurationStatus: string, inventoryDiscoveryEnabled: boolean = true) => {
  let configRoutes;
  const isMe1200 = modelName?.startsWith("ME1200");
  const offlineRadioNodeRoute = isMe1200 ? CONFIGUREROUTES.DU : CONFIGUREROUTES.RADIONODE;
  let isDas = isDasModel(modelName);
  const isMa3000 = modelName === "MA3000";
  const isMa6000 = modelName === "MA6000-G2";
  const isMa6200 = ['MA6200-G1', 'MA6200-G2'].includes(modelName);
  if(configurationStatus?.toLowerCase() === "offline") {
    if(isDas){
      configRoutes = [
        CONFIGUREROUTES.NODESUMMARY,
        ...(inventoryDiscoveryEnabled && isMa3000 ? [CONFIGUREROUTES.HCMINFO] : []),
        ...(inventoryDiscoveryEnabled && isMa6200 ? [CONFIGUREROUTES.CONTROLLERINFO] : []),
        ...(inventoryDiscoveryEnabled && isMa6000 ? [CONFIGUREROUTES.DCUINFO] : []),
        ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASRADIONODES] : []),
        ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASINVENTORY] : []),
        ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASTOPOLOGY] : []),
        CONFIGUREROUTES.SNMPDETAILS,
        ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.LOCATION] : []),
        CONFIGUREROUTES.WEB_TERMINAL,
      ];
    }
    else{
      configRoutes = [
      CONFIGUREROUTES.NODESUMMARY,
      CONFIGUREROUTES.IPINTERFACE,
      offlineRadioNodeRoute,
      nodeType === "SN" ? CONFIGUREROUTES.LTECELL : CONFIGUREROUTES.NRCELL,
      CONFIGUREROUTES.ZONES,
      CONFIGUREROUTES.LTEX2CONNECTION,
      CONFIGUREROUTES.SUPERSET,
      CONFIGUREROUTES.FILES,
      CONFIGUREROUTES.LOCATION,
    ]
    }

    return configRoutes;
  }
  if (isDas) {
    return [
      CONFIGUREROUTES.NODESUMMARY,
      ...(inventoryDiscoveryEnabled && isMa3000 ? [CONFIGUREROUTES.HCMINFO] : []),
      ...(inventoryDiscoveryEnabled && isMa6200 ? [CONFIGUREROUTES.CONTROLLERINFO] : []),
      ...(inventoryDiscoveryEnabled && isMa6000 ? [CONFIGUREROUTES.DCUINFO] : []),
      ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASRADIONODES] : []),
      ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASINVENTORY] : []),
      ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.DASTOPOLOGY] : []),
      CONFIGUREROUTES.SNMPDETAILS,
      ...(inventoryDiscoveryEnabled ? [CONFIGUREROUTES.LOCATION] : []),
      CONFIGUREROUTES.WEB_TERMINAL,
    ];
  }
  const isCu1000 = modelName === "CU-1000";
  const rnCuType = isCu1000
    ? []
    : isMe1200
      ? [CONFIGUREROUTES.DU]
      : modelName !== "SCSN-10000-3"
        ? [CONFIGUREROUTES.RADIONODE]
        : [CONFIGUREROUTES.DU, CONFIGUREROUTES.RADIONODE];
  const enableMmwCuLatest = isCu1000
    ? []
    : modelName !== "SCSN-10000-3"
      ? []
      : [CONFIGUREROUTES.INVENTORY, CONFIGUREROUTES.RN620, CONFIGUREROUTES.RFFEEDLATEST];
  const enableMmwCuType = isCu1000
    ? []
    : modelName !== "SCSN-10000-3"
      ? []
      : [CONFIGUREROUTES.INVENTORY, CONFIGUREROUTES.RN620, CONFIGUREROUTES.RFFEED];
  if (nodeType === "SN") {
    configRoutes = [
      CONFIGUREROUTES.NODESUMMARY,
      CONFIGUREROUTES.COMMISSIONING,
      CONFIGUREROUTES.IPINTERFACE,
      CONFIGUREROUTES.RADIONODE,
      CONFIGUREROUTES.LTECELL,
      CONFIGUREROUTES.ZONES,
      CONFIGUREROUTES.LTEX2CONNECTION,
      CONFIGUREROUTES.SUPERSET,
      CONFIGUREROUTES.FILES,
      CONFIGUREROUTES.LOCATION,
      CONFIGUREROUTES.SSH,
     
    ];
  } else if (compareVersions(swVersion, SCOS_VERSION_23_4_0)) {
    configRoutes =
      nodeType === "RCP"
        ? [
            CONFIGUREROUTES.NODESUMMARY,
            CONFIGUREROUTES.COMMISSIONING,
            ...enableMmwCuLatest,
            CONFIGUREROUTES.IPINTERFACE,
            ...rnCuType,
            CONFIGUREROUTES.NRCELL,
            CONFIGUREROUTES.ZONES,
            CONFIGUREROUTES.NGX2CONNECTION,
            CONFIGUREROUTES.SUPERSET,
            CONFIGUREROUTES.FILES,
            CONFIGUREROUTES.LOCATION,
                  CONFIGUREROUTES.SSH,
          ]
        : [
            CONFIGUREROUTES.NODESUMMARY,
            CONFIGUREROUTES.COMMISSIONING,
            // CONFIGUREROUTES.INVENTORY,
            // CONFIGUREROUTES.RN620,
            // CONFIGUREROUTES.RFFEEDLATEST,
            CONFIGUREROUTES.IPINTERFACE,
          ...rnCuType,
            CONFIGUREROUTES.NRCELL,
            CONFIGUREROUTES.ZONES,
            CONFIGUREROUTES.NGX2CONNECTION,
            CONFIGUREROUTES.SUPERSET,
            CONFIGUREROUTES.FILES,
            CONFIGUREROUTES.LOCATION,
                  CONFIGUREROUTES.SSH,
          ];
  } else {
    configRoutes =
      nodeType === "RCP"
        ? [
            CONFIGUREROUTES.NODESUMMARY,
            CONFIGUREROUTES.COMMISSIONING,
            ...enableMmwCuType,
            CONFIGUREROUTES.IPINTERFACE,
            ...rnCuType,
            CONFIGUREROUTES.NRCELL,
            CONFIGUREROUTES.ZONES,
            CONFIGUREROUTES.NGX2CONNECTION,
            CONFIGUREROUTES.SUPERSET,
            CONFIGUREROUTES.FILES,
            CONFIGUREROUTES.LOCATION,
                  CONFIGUREROUTES.SSH,
          ]
        : [
            CONFIGUREROUTES.NODESUMMARY,
            CONFIGUREROUTES.COMMISSIONING,
            // CONFIGUREROUTES.INVENTORY,
            // CONFIGUREROUTES.RN620,
            // CONFIGUREROUTES.RFFEED,
            CONFIGUREROUTES.IPINTERFACE,
          ...rnCuType,
            CONFIGUREROUTES.NRCELL,
            CONFIGUREROUTES.ZONES,
            CONFIGUREROUTES.NGX2CONNECTION,
            CONFIGUREROUTES.SUPERSET,
            CONFIGUREROUTES.FILES,
            CONFIGUREROUTES.LOCATION,
                  CONFIGUREROUTES.SSH,
          ];
  }

  //const dtype = Object.keys(nodeTypes).filter((item: string) => item !== nodeType)[0];

  //const names = Object.getOwnPropertyNames(CONFIGUREROUTES);
  return configRoutes;
};

export const prepareRadioNodeDataGrid = (data: Array<IRadioNodes>) => {
  return data?.map((element: IRadioNodes) => ({
    id: Number(element?.index) || 0,
    name: element?.name || "",
    description: element?.description || "",
    status: element?.operState || "",
    enable: handleBooleanStringValues(element?.enable) || false,
    ethernetId: element?.ethernetId || "",
    ipAddress: element?.ipAddress || "",
    model: element?.mfgData?.modelNumber || "",
    nodeType: element.nodeType || "",
    outerIpAddress: element.outerIpAddress || "",
    radios: element.radios || [],
    latitude: element.latitude || "",
    longitude: element.longitude || "",
    //radioUnit: element.radioUnit || null
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    // alarms: element?.alarms || "Unknown",
    //nrCell: element?.nrCell || "-",
  }));
};

export const prepareDuNodeDataGrid = (data: Array<IRadioNodes>) => {
  return data?.map((element: IRadioNodes) => ({
    id: Number(element?.index) || 0,
    name: element?.name || "",
    description: element?.description || "",
    status: element?.operState || "",
    enable: handleBooleanStringValues(element?.enable) || false,
    ethernetId: element?.ethernetId || "",
    ipAddress: element?.ipAddress || "",
    model: element?.mfgData?.modelNumber || "",
    nodeType: element.nodeType || "",
    outerIpAddress: element.outerIpAddress || "",
    radios: element.radios || [],
    radioUnit: element.radioUnit || null,
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    // alarms: element?.alarms || "Unknown",
    //nrCell: element?.nrCell || "-",
  }));
};

export const prepareNrCellDataGrid = (data: Array<INrCell>, zoneList?: IZoneDevice[]) => {
  return data?.map((element: INrCell) => ({
    id: Number(element?.index) || 0,
    name: element?.name || "",
    description: element?.description || "",
    status: element?.operState || "",
    enable: handleBooleanStringValues(element?.enable) || false,
    radioNode: element?.radioNode || "",
    dlBandwidth: element?.nrcellConfig?.ng?.ngran?.ngranRf?.dlBandwidth || "",
    zoneId: getZoneIdCellList(element?.index, zoneList || []) || "",
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    alarms: element?.alarms || "Unknown",
  }));
};

export const prepareZoneCellDataGrid = (data: Array<IZoneDevice>) => {
  return data?.map((element: IZoneDevice, index: number) => ({
    id: Number(index) || 0,
    zone: element?.zone || "",
    cellList: element?.cellList || [],
    scSpacingCommon: element?.scSpacingCommon || "",
    description: element?.description || "",
    nrcell: element?.nrcell || 0,
    barringforemergency: element?.barringforemergency || false,
    eARFCNUL: element?.eARFCNUL || "",
    ulBandwidth: element?.ulBandwidth || "",
    eARFCNDL: element?.eARFCNDL || "",
    dlBandwidth: element?.dlBandwidth || "",
    index: element.index || "",
    priority: element?.priority || "",
    zoneType: element?.zoneType || "",
  }));
};

export const prepareSnmpGroupCellDataGrid = (data: any) => {
  return data?.map((element: any, index: any) => ({
    id: Number(index) || 0,
    key: element?.key || "",
    parentKey: element?.parentKey || "",
    name: element?.name || "",
    port: element?.port || 0,
    ipAddressesList: element?.ipAddressesList || "",
    enabled: element?.enabled || false,
    community: element?.community || "",
    version: element?.version || "",
    contextName: element?.contextName || "",
    userName: element?.userName || "",
    authPassword: element?.authPassword || "",
    privPassword: element?.privPassword || "",
    authProtocol: element?.authProtocol || "",
    privProtocol: element?.privProtocol || "",
    enableHeartbeat: element?.enableHeartbeat || false,
    heartbeatInterval: element?.heartbeatInterval || 0,
    lastHeartbeatTrap: element?.lastHeartbeatTrap || "",
  }));
};

export const prepareEmailGroupCellDataGrid = (data: any) => {
  return data?.map((element: any, index: any) => ({
    id: Number(index) || 0,
    eventTypes: element?.eventTypes || "",
    email: element?.email || "",
    parentKey: element?.id?.parentKey || "",
    objectKey: element?.id?.objectKey || "",
    name: element?.id?.parentKey || "",
    emailAddresses: element?.email || "",
  }));
};

interface IItems {
  text: string;
  value: string;
}

export const getPeriod = (value: any) => {
  const period = repeatOptions.filter((item: IItems) => Number(item.value) === Number(value));
  return period[0]?.text || "";
};

export const prepareScheduledTasksCellDataGrid = (data: Array<ITaskDetails>) => {
  return data?.map((element: ITaskDetails, index: number) => ({
    id: Number(index) || 0,
    geoRegion: element?.geoRegion || "",
    name: element?.name || "",
    taskName: element?.taskName || "",
    period: getPeriod(element?.period),
    lastCompleted: element?.lastCompleted || 0,
    status: element?.status || "",
    nextTime: element?.nextTime || 0,
    description: element.description,
    createdBy: element?.createdBy || "",
    objectKey: element?.objectKey || "",
    taskType: element?.taskType || "",
  }));
};

export const prepareTaskLogDetailsCellDataGrid = (data: Array<ITaskLogDetailsPayload>, taskName: string) => {
  return data?.map((element: ITaskLogDetailsPayload, index: number) => ({
    id: Number(index) || 0,
    name: taskName || "",
    key: element?.key || "",
    percentage: element?.percentage || 0,
    status: element?.status || "",
    details: transfomDetail(element?.details) || "",
  }));
};

export const prepareTemplateCellDataGrid = (data: Array<ITemplates>) => {
  if(data?.length < 1) {
    return [];
  }
  return data?.filter((temp) => temp?.templateName?.toLowerCase() !== "nodeautotargets")?.map((element: ITemplates, index: number) => ({
    id: Number(index) || 0,
    key: element?.key || "",
    templateName: element?.templateName || "",
    description: element?.description || "",
    imageConstraints: element?.imageConstraints || "",
    nodeTypeKey: element?.nodeTypeKey || "",
    interfaceName: element?.interfacename || "",
    goldStandard: element?.goldStandard ?? false,
  }));
};

export const prepareCommTemplateCellDataGrid = (data: Array<ITemplates>, selectedDevice: string) => {
  if(data?.length < 1) {
    return [];
  }
  return data?.filter((temp) => temp?.templateName?.toLowerCase() !== "nodeautotargets")?.map((element: ITemplates, index: number) => ({
    id: Number(index) || 0,
    templateName: element?.templateName || "",
    description: element?.description || "",
    imageConstraints: element?.imageConstraints || "",
    domainKey: element?.domainKey || "Initial domain",
    nodeTypeKey: element?.nodeTypeKey || "",
    interfaceName: element?.interfacename || "",
    goldStandard: element?.goldStandard ?? false,
    deviceKey: element.templateDeviceMapProperties ? Object.keys(element.templateDeviceMapProperties).find((el) => el === selectedDevice) || "" : "",
  }));
};

export const prepareGlobalTemplatesDataGrid = (data: Array<any>) => {
  let arr = [];
  if (data?.length > 0) {
    arr = data?.filter((el) => el?.interfaceName?.toLowerCase() !== "vsnet");
  }
  return arr;
};

export const prepareAllTaskLogDataGrid = (data: Array<any>) => {
  return data?.map((element: any, index: number) => ({
    id: Number(index) || 0,
    name: element?.name || "",
    error: element?.error || "",
    startTime: element?.startTime || "",
    endTime: element?.endTime || 0,
    status: element?.status || "",
  }));
};

export const prepareTemplateLogDataGrid = (data: Array<any>) => {
  return data?.map((element: any, index: number) => ({
    id: Number(index) || 0,
    name: element?.parentKey || "",
    property: element?.property || "",
    status: element?.value || "",
  }));
};

export const prepareLocalTemplateCellDataGrid = (data: Array<ITemplates>, selectedDevice: string) => {
  let newArray: any = [];
  if (data.length > 0) {
    newArray = data?.filter((template: any) => {
      return (
        template.templateDeviceMapProperties &&
        Object.keys(template.templateDeviceMapProperties).includes(selectedDevice) &&
        template?.interfacename &&
        template?.interfacename?.toLowerCase() === "vsnet"
      );
    });
  }
  return newArray?.map((element: ITemplates, index: number) => ({
    id: Number(index) || 0,
    templateName: element?.templateName || "",
    description: element?.description || "",
    imageConstraints: element?.imageConstraints || "",
    nodeTypeKey: element?.nodeTypeKey || "",
    deviceKey:
      Object?.keys(element?.templateDeviceMapProperties || {})?.length > 0 ? `${Object?.keys(element?.templateDeviceMapProperties || {})[0]}` : "",
  }));
};

export const prepareTasksLogsCellDataGrid = (data: Array<ITaskLogData>) => {
  return data?.map((element: any, index: number) => ({
    id: Number(index) || 0,
    endDate: element?.endDate || 0,
    startDate: element?.startDate || 0,
    status: element?.status || "",
    taskExecutionKey: element?.taskExecutionKey || "",
    initiatedBy: element?.initiatedBy || "",
    total: element?.total || 0,
    success: element?.success || 0,
    failed: element?.failed || 0,
    idle: element?.idle || 0,
    running: element?.running || 0,
  }));
};

export const getZoneIdCellList = (target: string, data: IZoneDevice[]) => {
  if (data?.length > 0) {
    const filteredResult = data.filter((item) => {
      const cellList = item?.cellList
        ?.replace(/[^\d\s]/g, "")
        .trim()
        .split(" ");
      return cellList?.includes(target);
    });
    if (filteredResult?.length > 0) {
      return filteredResult?.map((item) => item?.index).join(", ");
    } else return "";
  } else return "";
};

export const prepareLteCellDataGrid = (data: Array<ILteCell>, zoneList?: Array<IZoneDevice>) => {
  return data?.map((element: ILteCell) => ({
    id: Number(element?.index) || 0,
    name: element?.name || "",
    description: element?.description || "",
    status: element?.operState || "",
    enable: handleBooleanStringValues(element?.enable) || false,
    radioNode: element?.radioNode || "",
    dlBandwidth: element?.lteCellConfig?.lte?.lteRan?.lteRanRf?.dlBandwidth || "",
    zoneId: getZoneIdCellList(element?.index, zoneList || []) || "",
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    alarms: element?.alarms || "Unknown",
  }));
};

export const prepareIPInterfaceDataGrid = (data: Array<IIPInterface>) => {
  return data?.map((element: IIPInterface, index: number) => ({
    id: index || 0,
    index: element?.index || "",
    description: element?.description || "",
    ipInterfaceIpAddress: element?.ipInterfaceIpAddress || "",
    vlanId: element?.vlanId || "",
    operState: element?.operState || "",
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    // ethernetId: element?.ethernetId || "00:40:05:1c:0e:9f",
    // alarms: element?.alarms || "Unknown",
  }));
};

export const prepareLteX2connectionsDataGrid = (data: Array<IltepeerInterfaces>) => {
  return data?.map((element: IltepeerInterfaces, index: number) => ({
    id: index || 0,
    index: element?.index || "",
    name: element?.name || "",
    //enable: Boolean(element?.enable) || false,
    //isENDCX2: Boolean(element?.isENDCX2) || false,
    enable : handleBooleanStringValues(element?.enable) || false,
    isENDCX2: handleBooleanStringValues(element?.isENDCX2) || false,
    connectionState: element?.connectionState || "",
    eNodeBID: element?.eNodeBID || "",
    localAddress: element?.localAddress || "0.0.0.0",
    address: element?.address || "0.0.0.0",
    plmnId: element?.plmnId || "",
    port: element?.port || "",
    tac: element?.tac || "",
    type: element?.type || "",
    gNodeBID: element?.gNodeBID || "",
    remoteAddress: element?.remoteAddress || "0.0.0.0",
    connectionId: element?.connectionId || "",
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    //alarms: element?.alarms || "Unknown",
  }));
};

export const prepareNgX2connectionsDataGrid = (data: Array<IngpeerInterfaces>) => {
  return data?.map((element: IngpeerInterfaces, index: number) => ({
    id: index || 0,
    index: element?.index || "",
    name: element?.name || "",
    enable: handleBooleanStringValues(element?.enable) || false,
    connectionState: element?.connectionState || "",
    eNodeBID: element?.eNodeBID || "",
    localAddress: element?.localAddress || ".0.0.0.0",
    plmnId: element?.plmnId || "",
    port: element?.port || "",
    remoteAddress: element?.remoteAddress || "0.0.0.0",
    connectionId: element?.connectionId || "",
    address: element?.address || "0.0.0.0",
    // TODO:
    /* These fields do not exist in the data object being returned from the server */
    // alarms: element?.alarms || "Unknown",
  }));
};

export const prepareRfFeedDataGrid = (data: Array<IRfFeed>) => {
  return data?.map((element: IRfFeed, index: number) => ({
    index: element?.index || "",
    type: element?.type || "",
    dlarfcn: element?.dlarfcn || "",
    ularfcn: element?.ularfcn || "",
    dlBandwidth: element?.dlBandwidth || "",
    ulBandwidth: element?.ulBandwidth || "",
    //dlAttenuation: element?.dlAttenuation || "",
  }));
};

export const prepareRn620DataGrid = (data: Array<IRn620>) => {
  return data?.map((element: IRn620, index: number) => ({
    index: element?.index || "",
    name: element?.name || "",
    onlineState: element?.onlineState || "",
    ethernetAddress: element?.ethernetAddress || "",
    serialNumber: element?.serialNumber || "",
    locationDescription: element?.locationDescription || "",
    modelName: element?.modelName || "",
    firmwareVersion: element?.firmwareVersion || "",
  }));
};

export const prepareSelectedDeviceDataFromDevceByKey = (data: IFullDeviceDetails) => {
  return {
    id: {
      // domainKey: data?.domainKey || "",
      objectKey: data?.objectKey || "",
      parentKey: data?.parentKey || "",
    },
    name: data?.name || "",
    configurationStatus: data?.configurationStatus || "",
    swVersion: data?.swVersion || "",
    ipAddress: data?.ipAddress || "",
    modelName: data?.modelName || "",
    sysUpTime: data?.sysUpTime || 0,
    nodeType: data?.nodeType || "",
    snConnected: String(data?.snConnected) || "",
  };
};

export const selectedSxProps: SxProps = {
  color: "var(--secondary)",
  backgroundColor: "var(--link)",
  "& .MuiSvgIcon-root.MuiSelect-icon": {
    color: "var(--secondary)",
  },
};

export const selectAlarmsProps = (componentName: string) => ({
  id: `${componentName}-alarm-selct`,
  labelId: `${componentName}-alarm-selct`,
  inputLabel: "ALARMS",
  selectOptions: SEVERITYLIST,
});
export const selectVersionProps = (componentName: string) => ({
  id: `${componentName}-swVersion-select`,
  labelId: `${componentName}-swVersion-select`,
  inputLabel: "VERSION",
  selectOptions: VERSIONLIST,
});
export const selectStatusProps = (componentName: string) => ({
  id: `${componentName}-status-select`,
  labelId: `${componentName}-status-select`,
  inputLabel: "Status",
  selectOptions: STATUSLIST,
});
export const selectMoreActionProps = (componentName: string) => ({
  id: `${componentName}-moreAction-select`,
  labelId: `${componentName}-moreAction-select`,
  inputLabel: "MORE ACTION",
  selectOptions: ["Action A", "Action B", "Action c"],
});

export const tableContainerSxProps = {
  overflow: "auto",
  borderTop: "1px solid var(--border)",
  borderBottom: "1px solid var(--border)",
  borderRadius: "5px",
};

export const zoneJSONData = [
  { id: 1, zone: "Snow", description: "Jon", nrcell: 124, enabled: true, erfcnul: "111", ulBandwidth: "112", erfcndl: "123", dlBandwidth: "133" },
  {
    id: 2,
    zone: "Lannister",
    description: "Cersei",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "112",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  {
    id: 3,
    zone: "Lannister",
    description: "Jaime",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "111",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  { id: 4, zone: "Stark", description: "Arya", nrcell: 124, enabled: true, erfcnul: "111", ulBandwidth: "112", erfcndl: "123", dlBandwidth: "133" },
  {
    id: 5,
    zone: "Targaryen",
    description: "Daenerys",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "112",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  {
    id: 6,
    zone: "Melisandre",
    description: "null",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "112",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  {
    id: 7,
    zone: "Clifford",
    description: "Ferrara",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "112",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  {
    id: 8,
    zone: "Frances",
    description: "Rossini",
    nrcell: 124,
    enabled: true,
    erfcnul: "111",
    ulBandwidth: "112",
    erfcndl: "123",
    dlBandwidth: "133",
  },
  { id: 9, zone: "Roxie", description: "Harvey", nrcell: 124, enabled: true, erfcnul: "111", ulBandwidth: "112", erfcndl: "123", dlBandwidth: "133" },
];

export const prepareInventoryDataGrid = (data: IInventory[]) => {
  let inventoryData: Array<IRowDataGridPro> = [];
  let hierarchyName: string[] = [];
  let uniqueId: number = 0;
  if (data.length > 0) {
    data.forEach((inventory: IInventory) => {
      uniqueId = uniqueId + 1;
      const {
        radioUnit,
        sectorDeviceInventory: { radioInputModules, sectorDeviceTree },
      } = inventory;
      //hierarchyName.push(`${radioUnit.name} (${radioUnit.ipAddress})`);
      hierarchyName = [`${radioUnit.name} (${radioUnit.ipAddress})`];
      const rdiounitObj = {
        id: uniqueId,
        hierarchy: hierarchyName,
        name: radioUnit?.name,
        index: radioUnit?.index,
        type: radioUnit?.nodeType || "",
        onlineState: radioUnit?.operState,
        serialNumber: radioUnit?.mfgData.serialNumber,
        ethernetAddress: radioUnit?.ethernetId,
        enable: radioUnit?.enable,
        modelName: radioUnit?.mfgData.modelNumber,
      };
      inventoryData.push(rdiounitObj);
      if (radioInputModules.length > 0) {
        radioInputModules.forEach((itemRIM: IRadioInputModule) => {
          uniqueId = uniqueId + 1;
          const radioInputModulesArr = {
            id: uniqueId,
            hierarchy: [...hierarchyName, `RadioinputModule_${itemRIM?.index}`],
            index: itemRIM?.index,
            type: "Radio Input Module",
            enable: itemRIM.enable,
            onlineState: itemRIM?.operState,
            name: `RFFeed_${itemRIM.rFFeedIndex}`,
          };
          inventoryData.push(radioInputModulesArr);
        });
      }
      uniqueId = uniqueId + 1;

      if (!!sectorDeviceTree) {
        const appendName =
          !!sectorDeviceTree?.sectorDevice?.name && sectorDeviceTree?.sectorDevice?.name !== "NULL" && sectorDeviceTree?.sectorDevice?.name !== "NA"
            ? sectorDeviceTree?.sectorDevice?.name
            : sectorDeviceTree?.sectorDevice?.serialNumber;
        const sectorDeviceTreeObj = {
          id: uniqueId,
          hierarchy: [...hierarchyName, `${appendName} (${sectorDeviceTree.sectorDevice.ipAddress})`],
          index: sectorDeviceTree?.sectorDevice?.index,
          deviceId: sectorDeviceTree?.sectorDevice?.deviceID,
          serialNumber: sectorDeviceTree?.sectorDevice?.serialNumber,
          name: sectorDeviceTree?.sectorDevice.name,
          ethernetAddress: sectorDeviceTree?.sectorDevice?.ethernetAddress,
          modelName: sectorDeviceTree?.sectorDevice?.modelName,
          firmwareVersion: sectorDeviceTree?.sectorDevice?.firmwareVersion,
          onlineState: sectorDeviceTree?.sectorDevice?.onlineState,
          type: sectorDeviceTree?.sectorDevice?.type,
          location: sectorDeviceTree?.sectorDevice?.locationDescription,
          parentPort: sectorDeviceTree?.sectorDevice?.parentPort,
        };
        inventoryData.push(sectorDeviceTreeObj);
        if (sectorDeviceTree.hasChildren && sectorDeviceTree.children.length > 0) {
          let hierarchyName1: string[] = [];
          hierarchyName1.push(`${appendName} (${sectorDeviceTree.sectorDevice.ipAddress})`);
          sectorDeviceTree.children.forEach((itemChildren: ISectorDeviceTree) => {
            uniqueId = uniqueId + 1;
            const appendName =
              !!itemChildren?.name && itemChildren?.name !== "NULL" && itemChildren?.name !== "NA"
                ? itemChildren?.name
                : itemChildren?.sectorDevice?.serialNumber;
            const newHierarchy = [...hierarchyName, ...hierarchyName1, `${appendName} (${itemChildren.sectorDevice.ipAddress})`];
            const sectorDeviceTreeObj = {
              id: uniqueId,
              hierarchy: newHierarchy,
              index: itemChildren?.sectorDevice?.index,
              deviceId: itemChildren?.sectorDevice?.deviceID,
              serialNumber: itemChildren?.sectorDevice?.serialNumber,
              name: itemChildren?.sectorDevice.name,
              ethernetAddress: itemChildren?.sectorDevice?.ethernetAddress,
              modelName: itemChildren?.sectorDevice?.modelName,
              firmwareVersion: itemChildren?.sectorDevice?.firmwareVersion,
              onlineState: itemChildren?.sectorDevice?.onlineState,
              type: itemChildren?.sectorDevice?.type,
              location: itemChildren?.sectorDevice?.locationDescription,
              parentPort: itemChildren?.sectorDevice?.parentPort,
            };
            inventoryData.push(sectorDeviceTreeObj);

            if (itemChildren.hasChildren) {
              let hierarchyName2: string[] = [];
              hierarchyName2.push(`${appendName} (${itemChildren.sectorDevice.ipAddress})`);
              itemChildren.children.forEach((itemChildren: ISectorDeviceTree) => {
                uniqueId = uniqueId + 1;
                const appendName =
                  !!itemChildren?.name && itemChildren?.name !== "NULL" && itemChildren?.name !== "NA"
                    ? itemChildren?.name
                    : itemChildren?.sectorDevice?.serialNumber;
                const newHierarchy = [
                  ...hierarchyName,
                  ...hierarchyName1,
                  ...hierarchyName2,
                  `${appendName} (${itemChildren.sectorDevice.ipAddress})`,
                ];
                const sectorDeviceTreeObj = {
                  id: uniqueId,
                  hierarchy: newHierarchy,
                  index: itemChildren?.sectorDevice?.index,
                  deviceId: itemChildren?.sectorDevice?.deviceID,
                  serialNumber: itemChildren?.sectorDevice?.serialNumber,
                  name: itemChildren?.sectorDevice.name,
                  ethernetAddress: itemChildren?.sectorDevice?.ethernetAddress,
                  modelName: itemChildren?.sectorDevice?.modelName,
                  firmwareVersion: itemChildren?.sectorDevice?.firmwareVersion,
                  onlineState: itemChildren?.sectorDevice?.onlineState,
                  type: itemChildren?.sectorDevice?.type,
                  location: itemChildren?.sectorDevice?.locationDescription,
                  parentPort: itemChildren?.sectorDevice?.parentPort,
                };
                inventoryData.push(sectorDeviceTreeObj);
              });
            }
          });
        }
      }
    });
  }
  return inventoryData;
};

export const prepareDrawerCounts = (drawerCounts: IdrawerCounts, nodeType?: string) => {
  let dataGridHeader: { key: string; downValue: number; upValue: number }[] = [];
  if (!!drawerCounts && nodeType) {
    if (nodeType === "SN") {
      dataGridHeader = [
        { key: "Radio Nodes", downValue: drawerCounts?.rn_Down || 0, upValue: drawerCounts?.rn_Up || 0 },
        { key: "Lte Cells", downValue: drawerCounts?.lte_Down || 0, upValue: drawerCounts?.lte_Up || 0 },
        { key: "X2 Connections", downValue: drawerCounts?.lte_X2_Disconnected || 0, upValue: drawerCounts?.lte_X2_Connected || 0 },
      ];
    } else {
      const isME1200 = nodeType === "ME1200";
      dataGridHeader = [
        { key: isME1200 ? "DUs" : "Radio Nodes", downValue: isME1200 ? (drawerCounts?.du_Down || 0) : (drawerCounts?.rn_Down || 0), upValue: isME1200 ? (drawerCounts?.du_Up || 0) : (drawerCounts?.rn_Up || 0) },
        { key: "Nr Cells", downValue: drawerCounts?.nr_Disabled || 0, upValue: drawerCounts?.nr_Enabled || 0 },
        { key: "X2 Connections", downValue: drawerCounts?.ng_X2_Disconnected || 0, upValue: drawerCounts?.ng_X2_Connected || 0 },
      ];
      
    }
  }

  return dataGridHeader;
};

export const prepareDeviceDataCountForSummary = (deviceCounts: IdeviceCounts, nodeType?: string) => {
  let dataGridHeader: { key: string; downValue: number; upValue: number }[] = [];
  if (!!deviceCounts && nodeType) {
    if (nodeType === "SN") {
      dataGridHeader = [
        { key: "IP Interfaces", downValue: deviceCounts?.lan_Down || 0, upValue: deviceCounts?.lan_Up || 0 },
        { key: "Radio Nodes", downValue: deviceCounts?.rn_Down || 0, upValue: deviceCounts?.rn_Up || 0 },
        { key: "LTE Cells", downValue: deviceCounts?.lte_Down || 0, upValue: deviceCounts?.lte_Up || 0 },
      ];
    } else {
      const isME1200 = nodeType === "ME1200";
      dataGridHeader = [
        { key: "IP Interfaces", downValue: deviceCounts?.lan_Down || 0, upValue: deviceCounts?.lan_Up || 0 },
        { key: isME1200 ? "DUs" : "Radio Nodes", downValue: isME1200 ? (deviceCounts?.du_Down || 0) : (isME1200 ? (deviceCounts?.du_Down || 0) : (deviceCounts?.rn_Down || 0)), upValue: isME1200 ? (deviceCounts?.du_Up || 0) : (isME1200 ? (deviceCounts?.du_Up || 0) : (deviceCounts?.rn_Up || 0)) },
        { key: "NR Cells", downValue: deviceCounts?.nr_Disabled || 0, upValue: deviceCounts?.nr_Enabled || 0 },
      ];
    }
  }

  return dataGridHeader;
};

export const prepareCellKpiDataGrid = (data: Array<any>) => {
  return data?.map((element: any, index: number) => ({
    id: Number(index) || 0,
    cell_name: element?.cell_name || "",
    dl_gtp_data_volume: element?.dl_gtp_data_volume || 0,
    erab_drop_count: element?.erab_drop_count || 0,
    erab_drop_rate: element?.erab_drop_rate || 0,
    erab_establishment_attempts: element?.erab_establishment_attempts || 0,
    erab_establishment_sr: element?.erab_establishment_sr || 0,
    erab_establishment_success: element?.erab_establishment_success || 0,
    hand_in_attempts: element?.hand_in_attempts || 0,
    hand_in_success: element?.hand_in_success || 0,
    hand_in_success_rate: element?.hand_in_success_rate || 0,
    inter_frequency_x1_ho: element?.inter_frequency_x1_ho || 0,
    inter_frequency_x1_ho_attempts: element?.inter_frequency_x1_ho_attempts || 0,
    inter_frequency_x1_ho_success: element?.inter_frequency_x1_ho_success || 0,
    lte_cell_availability: element?.lte_cell_availability || 0,
    max_endc_connected_ue_sessions: element?.max_endc_connected_ue_sessions || 0,
    rrc_connection_establishment_attempts: element?.rrc_connection_establishment_attempts || 0,
    rrc_connection_establishment_sr: element?.rrc_connection_establishment_sr || 0,
    rrc_connection_establishment_success: element?.rrc_connection_establishment_success || 0,
    scos_version: element?.scos_version || "unknown",
    sg_nb_addition_attempts: element?.sg_nb_addition_attempts || 0,
    sg_nb_addition_sr: element?.sg_nb_addition_sr || 0,
    sg_nb_addition_success: element?.sg_nb_addition_success || 0,
    site_name: element?.site_name || "",
    ul_gtp_data_volume: element?.ul_gtp_data_volume || 0,
    volte_drop_count: element?.volte_drop_count || 0,
    volte_drop_rate: element?.volte_drop_rate || 0,
    volte_establishment_attempts: element?.volte_establishment_attempts || 0,
    volte_establishment_sr: element?.volte_establishment_sr || 0,
    volte_establishment_success: element?.volte_establishment_success || 0,
  }));
};

export const prepareNrCellKpiDataGrid = (data: Array<any>) => {
  return data?.map((element: any, index: number) => ({
    id: Number(index) || 0,
    cell_name: element?.cell_name || "",
    dl_s1u_data_volume: element?.dl_s1u_data_volume || 0,
    endc_ue_session_count: element?.endc_ue_session_count || 0,
    endc_ue_session_drop_rate: element?.endc_ue_session_drop_rate || 0,
    intra_cu_handover_attempts: element?.intra_cu_handover_attempts || 0,
    intra_cu_handover_success: element?.intra_cu_handover_success || 0,
    intra_cu_handover_success_rate: element?.intra_cu_handover_success_rate || 0,
    max_number_of_endc_sessions: element?.max_number_of_endc_sessions || 0,
    nr_cell_availability: element?.nr_cell_availability || 0,
    scos_version: element?.scos_version || "unknown",
    sgnb_addition_attempts: element?.sgnb_addition_attempts || 0,
    sgnb_addition_request_ack: element?.sgnb_addition_request_ack || 0,
    sgnb_addition_sr: element?.sgnb_addition_sr || 10,
    site_name: element?.site_name || "",
    ul_s1u_data_volume: element?.ul_s1u_data_volume || 0
  }));
};

export const prepareCommissioningGridData = (nodeType: string) => {
  let dataGridHeader: ICommissioningData[] = [];
  dataGridHeader = [
    {
      id: 1,
      key: "upgrade",
      label: "Upgrade",
      versions: {
        currentVersion: "23.3.0",
        newVersion: "",
      },
      isTemplate: false,
      buttonText: ["upgrade"],
    },
    {
      id: 2,
      key: "global",
      label: "Global template",
      versions: {
        currentVersion: "",
        newVersion: "",
      },
      isTemplate: true,
      buttonText: ["apply"],
    },
    {
      id: 3,
      key: "local",
      label: "Local/bulk template",
      versions: {
        currentVersion: "10.123.11",
        newVersion: "1.1.12",
      },
      isTemplate: true,
      templateName: "",
      buttonText: ["import", "apply"],
    },
    {
      id: 4,
      key: "remScan",
      label: "Start Rem Scan",
      versions: {
        currentVersion: "",
        newVersion: "",
      },
      isTemplate: false,
      buttonText: ["stop", "start"],
    },
  ];
  if (nodeType !== "SN") {
    dataGridHeader?.pop();
  }
  return dataGridHeader;
};

export const prepareAlarmCountData = (data: IFullDeviceDetails) => {
  const dataGrid: Array<any> = [
    { key: "MAJOR", downValue: data?.majorAlarmCount || 0 },
    { key: "CRITICAL", downValue: data?.criticalAlarmCount || 0 },
  ];
  return dataGrid;
};

export const prepareDeviceConfigDetail = (data: IFullDeviceDetails, uptime: string) => {
  // Handle null/undefined data
  if (!data) {
    return {
      summary: [],
      status: [],
      location: [],
      hardware: [],
      settings: [],
    };
  }
  
  const enableMmwCuType = data?.nodeType !== "SN" && data?.modelName !== "SCSN-10000-3";
  const enableRCPDUMode = data?.swVersion ? compareVersions(data?.swVersion, SCOS_VERSION_23_4_0) && data?.nodeType !== "SN" : false;
  const isEditOfflineDevice = data?.configurationStatus?.toLowerCase() === "offline";
  const isDas = isDasModel(data?.modelName);
  const rcpDuMode: Array<IDataGridItem> = enableMmwCuType
    ? []
    : enableRCPDUMode
    ? [
        { key: "RCP MODE", value: data?.rcpMode || "", editIcon: !isEditOfflineDevice && enableRCPDUMode },
        { key: "DU MODE", value: data?.duMode || "", editIcon: !isEditOfflineDevice && enableRCPDUMode },
      ]
    : [];
  const dataGrid: IDataGrid = {
    summary: [],
    status: [],
    location: [],
    hardware: [],
    settings: [],
  };

  // summary
  dataGrid.summary.push({ key: "HOST NAME", value: data?.name || "", editIcon: !isEditOfflineDevice });
  dataGrid.summary.push({ key: "LOCATION", value: data?.locationId || "", editIcon: false });
  if(!isDas){
    dataGrid.summary.push({ key: "DESCRIPTION", value: data?.description || "", editIcon: !isEditOfflineDevice });
    dataGrid.summary.push({ key: "CONTACT", value: data?.contact || "", editIcon: !isEditOfflineDevice });
    dataGrid.summary.push({ key: "CLUSTER ID", value: data?.clusterId || "", editIcon: !isEditOfflineDevice });
  }
  dataGrid.summary.push({ key: "MANAGEMENT IP", value: data?.ipAddress || "", editIcon: false });
  dataGrid.summary.push({ key: "DEPLOYMENT TYPE", value: !!data?.deployment ? data?.deployment : "", editIcon: true });
  dataGrid.summary.push({ key: "LABEL", value: (data?.labelNames || ""), editIcon: true });
  dataGrid.summary.push({ key: "REGION", value: data?.geoRegionName || "Ungrouped", editIcon: false, });
  

  dataGrid.status.push({ key: "STATUS", value: data?.status === 19 ? "Disconnected" : "Connected", editIcon: false });
  dataGrid.status.push({ key: "CONFIGURATION STATUS", value: data?.configurationStatus || "", editIcon: false });
  if(!isDas){
    dataGrid.status.push({ key: "LAST INFORM", value: dayjs(data?.lastInform).format(DATEFORMATS.TS) || "", editIcon: false });
    dataGrid.status.push({ key: "UPTIME", value: uptime || "", editIcon: false });
    dataGrid.status.push({ key: "OPERATING MODE", value: data?.operationMode || "InService", editIcon: !isEditOfflineDevice });
  dataGrid.status.push({ key: "KPI SYNC STATUS", value: data?.pmCollected ? "Successful" : "FAIL", editIcon: false });
  
  }
  

  dataGrid.location.push({ key: "LOCATION", value: data?.location || "", editIcon: !isEditOfflineDevice });
  dataGrid.location.push({ key: "LATITUDE", value: data?.latitude?.toString() || "", editIcon: isDas || !isEditOfflineDevice });
  dataGrid.location.push({ key: "LONGITUDE", value: data?.longitude?.toString() || "", editIcon: isDas|| !isEditOfflineDevice });
  

  // Hardware section
  dataGrid.hardware.push({ key: "MODEL#", value: data?.modelName || "", editIcon: false });
  dataGrid.hardware.push({ key: "SERIAL#", value: data?.serialNumber || "", editIcon: false });
  
  if(!isDas){
    dataGrid.hardware.push({ key: "MANAGEMENT SERVER", value: data?.managementServerURL || "", editIcon: false });
    dataGrid.hardware.push({ key: "VERSION", value: data?.swVersion || "", editIcon: false });
  } else {
    // For MA3000 (DAS), show SW version from servicesdevice table (updated from HCM discovery)
    dataGrid.hardware.push({ key: "SW VERSION", value: data?.swVersion || "", editIcon: false });
  }

  if(!isDas){
    dataGrid.settings.push({ key: "PCI LOCK ENABLED", value: data?.pciLockEnable || "", editIcon: !isEditOfflineDevice });
    dataGrid.settings.push({ key: "NOTIFICATIONS", value: !!data?.groupKey ? data?.groupKey : "System Default", editIcon: true });
    dataGrid.settings = [...dataGrid.settings, ...rcpDuMode];
  }
  
  return dataGrid;
};

export const isDasModel = (modelName: string): boolean => {
  if (!modelName) {
    return false;
  }
  for (let index = 0; index < DasModelNames.length; index++) {
    if(modelName === DasModelNames[index].value){
      return true;  
    }
  }
  return false;
} 

export const prepareEmbeddedCellGrid = (data: any) => {
  const dataGrid: any = {
    summary: [
      { key: "HOSTNAME", value: data?.name || "", editIcon: false },
      { key: "MANAGEMENT IP", value: data?.ipAddress || "", editIcon: false },
      { key: "DESCRIPTION", value: data?.description || "", editIcon: false },
      { key: "CONTACT", value: data?.contact || "", editIcon: false },
    ],
  };
  return dataGrid;
};

export const getRadioUnitIndexList = (data: IInventory[]) => {
  let unitIndex: string[] = [];
  if (data.length > 0) {
    data.forEach((element: IInventory) => {
      unitIndex.push(element.radioUnit.index);
    });
  }
  return unitIndex;
};

export const checkRadioValueToUpdateOnly = (formValues: any, initialValues: any) => {
  let updateOnlyRadio = false;
  for (const key in formValues) {
    if (formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
      updateOnlyRadio = true;
      break;
    }
  }
  return updateOnlyRadio;
};

export const handleValidateProvision = (val: number, rnList: IRadioNodesDataGridRow[]) => {
  let isValid = false;
  let radioIndexes = rnList?.map((el) => el?.id);
  if (radioIndexes?.includes(val) || val >= PROVISION_CHECK_VAL) {
    isValid = false;
  } else {
    isValid = true;
  }
  return isValid;
};

//returns true if Software version is greater than or equal to target version
export const compareVersions = (swVersion: string, targetVersion: string) => {
  const versionRegex = /^(\d+)\.(\d+)\.(\d+)$/;
  const swMatches = swVersion.match(versionRegex);
  const targetMatches = targetVersion.match(versionRegex);

  if (!swMatches || !targetMatches) {
    // Invalid version format
    return false;
  }

  const swMajor = parseInt(swMatches[1]);
  const swMinor = parseInt(swMatches[2]);
  const swPatch = parseInt(swMatches[3]);

  const targetMajor = parseInt(targetMatches[1]);
  const targetMinor = parseInt(targetMatches[2]);
  const targetPatch = parseInt(targetMatches[3]);

  if (swMajor > targetMajor) {
    return true;
  } else if (swMajor === targetMajor) {
    if (swMinor > targetMinor) {
      return true;
    } else if (swMinor === targetMinor) {
      if (swPatch >= targetPatch) {
        return true;
      }
    }
  }

  return false;
};

export const resetRnCellInfo: IRadioNodeCellConfig = {
  rnCellData: {
    operators: [],
    radios: 0,
    type: "",
    bandCombinationSupported: [],
    radioBandOne: null,
    radioBandTwo: null,
  },
  bandData: [],
};

export const initialRadioLteCellInfo: IRnLteCellConfig = {
  index: 0,
  enable: "",
  name: "",
  description: "",
  operState: "",
  confState: "",
  radio: 0,
  radioNode: "",
  lteCellConfig: {
    index: "",
    lte: {
      index: "",
      lteRan: {
        index: "",
        lteRanCommon: {
          index: "",
          eci: "",
          cellId: "",
        },
        lteRanRf: {
          index: 0,
          earfcndl: 0,
          dlBandwidth: 0,
          referenceSignalPowerInUse: 0,
          modeInUse: "",
          phyCellIDInUse: 0,
        },
      },
    },
    unlicensedLte: {
      index: "",
      unlicensedLteRan: {
        index: "",
        unlicensedLteRanRf: {
          index: "",
          earfcndls: [],
        },
      },
    },
  },
};

export const initialDuCellInfo = {
  index: "",
  enable: "",
  name: "",
  description: "",
  operState: "",
  confState: "",
  radio: "",
  radioNode: "",
  nrcellConfig: {
    index: "",
    ng: {
      index: "",
      ngran: {
        index: "",
        ngranCommon: {
          index: "",
          eci: null,
          cellId: "",
        },
        ngranRf: {
          index: "",
          earfcndl: "",
          dlBandwidth: "",
          referenceSignalPowerInUse: "",
          modeInUse: "",
          phyCellIDInUse: "",
        },
      },
    },
  },
};

export const resetCellInfo: IRnCellInfo = {
  cellId: 0,
  cellName: "",
  cellPci: 0,
  cellDescription: "",
  power: 0,
  actualOta: "",
  zone: "",
  band: "",
  status: "",
};

export const resetDuCellInfo: IDuCellInfo = {
  cellId: "",
  cellName: "",
  cellPci: "",
  cellDescription: "",
  txPower: "",
  commonCellId: "",
  power: "",
  pbchPower: "",
  zone: "",
  status: "",
};

export const resetRnDrawerInfo: IRadioNodesDataGridRow = {
  id: 0,
  name: "",
  description: "",
  status: "",
  enable: false,
  ethernetId: "",
  ipAddress: "",
  model: "",
  nodeType: "",
  outerIpAddress: "",
  location: "",
  latitude: "",
  longitude: "",
  radios: [],
};

export const prepareCustomEventCellDataGrid = (data: any) => {
  return data?.map((element: any, index: any) => ({
    id: Number(index) || 0,
    name: element?.name || "",
    description: element?.description || "",
    triggerType: element?.triggerType || "",
    matchString: element?.matchString || "",
    regex: element?.regex || false,
    matchCase: element?.matchCase || false,
  }));
};

export const customEventPayload = (formData: ICustomEvent) => ({
  name: formData?.name || "",
  description: formData?.description || "",
  matchString: formData?.matchString || "",
  regex: formData?.regex || false,
  matchCase: formData?.matchCase || false,
});

export const handleOldZoneData = (zones: IOption[], zoneList: IZoneDevice[]) => {
  let zonesToUpdate: IZoneDevice[] = [];
  if (zones?.length > 0 && zoneList?.length > 0) {
    zonesToUpdate = zoneList?.filter((item) => {
      return zones?.find((obj) => obj?.value === item?.index);
    });
  }
  return zonesToUpdate;
};

export const handleCreateCellList = (cellList: string, cellId: string, type?: string) => {
  if (!cellList) return "[ ]";
  if (cellId) {
    let celllistData = cellList
      ?.replace(/[^\d\s]/g, "")
      .trim()
      .split(" ");
    if (type !== "add") {
      let listData = celllistData?.filter((el) => el !== cellId);
      if (listData?.length > 0) {
        return handleZonesSelection(listData);
      }
      return handleZonesSelection(listData);
    } else {
      let listData = celllistData?.filter((el) => el !== "");
      if (listData?.length > 0) {
        if (listData?.includes(cellId)) {
          return handleZonesSelection(listData);
        } else listData.push(cellId);
      } else listData.push(cellId);
      return handleZonesSelection(listData);
    }
  } else return cellList;
};

const handleZonesSelection = (zones: string[]) => {
  let zoneValues = "[ ]";
  if (zones?.length > 0) {
    zoneValues = "[ " + zones?.join(" ") + " ]";
  }
  return zoneValues;
};

export const handleNewCellListData = (
  formData: IRnCellInfo | IDuCellInfo,
  zones: IOption[],
  initialZones: IOption[],
  zoneList: IZoneDevice[],
  isRemove?: boolean
) => {
  let newArray: ICellListData[] = [];
  let arr1 = isRemove ? [] : zones?.map((el) => el?.value);
  let arr2 = initialZones?.map((el) => el?.value);
  let added_Values = new Set(arr1.filter((x) => !arr2.includes(x)));
  let deleted_Values = new Set(arr2.filter((x) => !arr1.includes(x)));

  let obj = {
    added_Values: Array.from(added_Values) || [],
    deleted_Values: Array.from(deleted_Values) || [],
  };

  if (obj?.added_Values?.length > 0 && formData?.cellId) {
    let zonesToUpdate = zoneList?.filter((item: IZoneDevice) => {
      return obj?.added_Values?.find((el) => el === item?.index);
    });
    zonesToUpdate?.map((el) => {
      let obj: ICellListData = {
        oldCellList: el?.cellList,
        newCellList: handleCreateCellList(el?.cellList, formData.cellId?.toString() || "", "add"),
        zoneIndex: el?.index?.toString() || "",
      };
      return newArray?.push(obj);
    });
  }

  if (obj?.deleted_Values?.length > 0 && formData?.cellId) {
    let zonesToUpdate = zoneList?.filter((item: IZoneDevice) => {
      return obj?.deleted_Values?.find((el) => el === item?.index);
    });
    zonesToUpdate?.map((el) => {
      let obj: ICellListData = {
        oldCellList: el?.cellList,
        newCellList: handleCreateCellList(el?.cellList, formData.cellId?.toString() || "", "delete"),
        zoneIndex: el?.index?.toString() || "",
      };
      return newArray?.push(obj);
    });
  }

  return newArray;
};

export const checkSimilarity = (array1: IOption[], array2: IOption[]) => {
  let arr1 = array1?.map((el) => el?.value);
  let arr2 = array2?.map((el) => el?.value);
  if (arr1.length !== arr2.length) {
    return false;
  }

  const count1: { [key: number]: number } = {};
  const count2: { [key: number]: number } = {};

  arr1.forEach((num: any) => {
    count1[num] = count1[num] + 0 + 1;
  });

  arr2.forEach((num: any) => {
    count2[num] = count2[num] + 0 + 1;
  });

  return JSON.stringify(count1) !== JSON.stringify(count2);
};

export const handleOnlyCellUpdatedValues = (formValues: any, initialValues: any) => {
  const updateCellFields: IKeyValueAny = {};
  for (const key in formValues) {
    if (key !== "status" && formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
      updateCellFields[key] = formValues[key];
    }
  }
  return updateCellFields;
};